import React from "react";
import {
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  LABEL_STATUS_IMPORTACAO,
  STATUS_IMPORTACAO_ATIVOS,
  formatarCodigoBancoImportacao,
  formatarCnpjImportacao,
  formatarPercentualImportacao,
  formatarPeriodoImportacao,
  formatarSaldoMonetarioImportacao,
  resolverAgenciaImportacao,
  resolverCelulaSituacaoImportacao,
  resolverContaImportacao,
  resolverSaldoConciliadoImportacao,
  resolverSaldoFinalImportacao,
} from "../../utils/importacoesExtrato.util";
import styles from "./styles.module.css";

function CampoDetalhe({ label, children }) {
  return (
    <div className={styles.detalheCampo}>
      <span className={styles.detalheCampoLabel}>{label}</span>
      <span className={styles.detalheCampoValor}>{children ?? "—"}</span>
    </div>
  );
}

function resolverChipStatus(status) {
  const label = LABEL_STATUS_IMPORTACAO[status] || status || "—";
  if (status === "concluido") return { label, color: "success" };
  if (status === "erro") return { label, color: "error" };
  if (status === "em_execucao") return { label, color: "warning" };
  if (status === "pendente") return { label, color: "default" };
  if (status === "cancelado") return { label, color: "default" };
  return { label, color: "default" };
}

export default function ModalDetalheImportacao({
  loading,
  detalheJob,
  detalheLogs = [],
}) {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Carregando…
      </Typography>
    );
  }

  if (!detalheJob) {
    return <Typography variant="body2">Nenhum detalhe disponível.</Typography>;
  }

  const status = detalheJob.status || "";
  const chip = resolverChipStatus(status);
  const mensagemErro = detalheJob.mensagem_erro
    ? String(detalheJob.mensagem_erro).trim()
    : "";
  const isErro = status === "erro" || Boolean(mensagemErro);
  const isSucesso = status === "concluido";
  const emAndamento = STATUS_IMPORTACAO_ATIVOS.has(status);
  const situacao = resolverCelulaSituacaoImportacao(detalheJob);
  const saldoConciliado = resolverSaldoConciliadoImportacao(detalheJob);
  const saldoFinal = resolverSaldoFinalImportacao(detalheJob);
  const periodo = formatarPeriodoImportacao(detalheJob);
  const mostrarResultado =
    status === "concluido" ||
    status === "erro" ||
    periodo !== "—" ||
    saldoConciliado != null ||
    saldoFinal != null;

  return (
    <div className={styles.detalheModal}>
      <div
        className={`${styles.detalheStatusBar} ${
          isSucesso
            ? styles.detalheStatusSucesso
            : isErro
              ? styles.detalheStatusErro
              : styles.detalheStatusNeutro
        }`}
        role={isErro && mensagemErro ? "alert" : undefined}
      >
        <Chip size="small" label={chip.label} color={chip.color} />
        {isErro && mensagemErro ? (
          <Typography variant="body2" className={styles.detalheStatusMensagem}>
            {mensagemErro}
          </Typography>
        ) : null}
        {emAndamento && situacao.modo === "progresso" ? (
          <div className={styles.detalheProgresso}>
            <LinearProgress
              variant={
                situacao.percentual != null ? "determinate" : "indeterminate"
              }
              value={situacao.percentual ?? 0}
              className={styles.detalheProgressoBarra}
            />
            <Typography variant="caption" color="text.secondary">
              {formatarPercentualImportacao(situacao.percentual)}
              {situacao.etapa ? ` · ${situacao.etapa}` : ""}
            </Typography>
          </div>
        ) : null}
      </div>

      <section className={styles.detalheSecao}>
        <Typography variant="subtitle2" className={styles.detalheSecaoTitulo}>
          Empresa
        </Typography>
        <div className={styles.detalheEmpresa}>
          <Typography variant="body1" className={styles.detalheRazaoSocial}>
            {detalheJob.razao_social || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatarCnpjImportacao(detalheJob.cnpj)}
          </Typography>
        </div>
      </section>

      <section className={styles.detalheSecao}>
        <Typography variant="subtitle2" className={styles.detalheSecaoTitulo}>
          Arquivo
        </Typography>
        <div className={styles.detalheEmpresa}>
          <Typography variant="body1" className={styles.detalheRazaoSocial}>
            {detalheJob.nome_arquivo || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(detalheJob.tipo_arquivo || "—").toString().toUpperCase()}
          </Typography>
        </div>
      </section>

      <section className={styles.detalheSecao}>
        <Typography variant="subtitle2" className={styles.detalheSecaoTitulo}>
          Banco
        </Typography>
        <div className={styles.detalheLinha}>
          <CampoDetalhe label="Banco">
            {formatarCodigoBancoImportacao(detalheJob.codigo_banco)}
          </CampoDetalhe>
          <CampoDetalhe label="Agência">
            {resolverAgenciaImportacao(detalheJob) || "—"}
          </CampoDetalhe>
          <CampoDetalhe label="Conta">
            {resolverContaImportacao(detalheJob) || "—"}
          </CampoDetalhe>
        </div>
      </section>

      {mostrarResultado ? (
        <section className={styles.detalheSecao}>
          <Typography variant="subtitle2" className={styles.detalheSecaoTitulo}>
            Resultado
          </Typography>
          <div className={styles.detalheLinha}>
            <CampoDetalhe label="Período">{periodo}</CampoDetalhe>
            <CampoDetalhe label="Saldo final">
              {formatarSaldoMonetarioImportacao(saldoFinal)}
            </CampoDetalhe>
            <CampoDetalhe label="Saldo conciliado">
              <span
                className={
                  detalheJob.fechamento_ok === false
                    ? styles.saldoDivergente
                    : styles.saldoOk
                }
              >
                {formatarSaldoMonetarioImportacao(saldoConciliado)}
                {detalheJob.fechamento_ok === false ? " (divergente)" : ""}
              </span>
            </CampoDetalhe>
          </div>
          {detalheJob.divergencia != null ? (
            <Typography
              variant="body2"
              className={
                Number(detalheJob.divergencia) === 0
                  ? styles.saldoConciliadoOk
                  : styles.saldoDivergente
              }
              sx={{ mt: 0.5 }}
            >
              Divergência:{" "}
              {formatarSaldoMonetarioImportacao(detalheJob.divergencia)}
            </Typography>
          ) : null}
        </section>
      ) : null}

      {detalheLogs.length > 0 ? (
        <section className={styles.detalheSecao}>
          <Typography variant="subtitle2" className={styles.detalheSecaoTitulo}>
            Logs
          </Typography>
          <ul className={styles.logLista}>
            {detalheLogs.map((log, idx) => (
              <li key={idx}>
                [{log.etapa || "—"}] {log.mensagem || ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
